import { Badge } from "@circleco/compass/components/Badge";
import type { BadgeProps } from "@circleco/compass/components/Badge";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { Divider } from "@/react/components/search/Divider";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { LoaderRow } from "@/react/components/shared/LoaderRow";
import type { CommunityMember } from "@/react/types";
import type { Person } from "@/react/types/AudienceList";

import { ExpandableAccordionSkeleton } from "./ExpandableAccordionSkeleton";
import { ExpandableListItem } from "./ExpandableListItem";

interface ExpandableAccordionProps {
  className?: string;
  fetchNextPage?: () => void;
  hasMore?: boolean;
  incrementCount?: number;
  initialVisibleCount?: number;
  isFetchingNextPage?: boolean;
  isLoading: boolean;
  items: ExpandableAccordionItem[];
  listTestId?: string;
  testId?: string;
  title: string;
  totalCount?: number;
  totalCountVariant?: BadgeProps["variant"];
}

export interface ExpandableAccordionItem {
  id: string;
  person: Person;
  type: "recommendation" | "connection";
}

export const ExpandableAccordion = ({
  title,
  items,
  totalCount,
  isLoading,
  hasMore = false,
  fetchNextPage,
  isFetchingNextPage = false,
  initialVisibleCount = 2,
  incrementCount = 2,
  className,
  totalCountVariant = "secondary",
  testId,
  listTestId,
}: ExpandableAccordionProps) => {
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);

  const handleShowMore = () => {
    const newVisibleCount = visibleCount + incrementCount;
    setVisibleCount(newVisibleCount);

    const shouldFetchNextPage =
      fetchNextPage && hasMore && newVisibleCount >= items.length;

    // Only fetch next page if there are more pages to fetch
    if (shouldFetchNextPage) {
      fetchNextPage();
    }
  };

  const visibleItems = items.slice(0, visibleCount);
  // Show "Show More" if there are more items already fetched OR if there are more pages to fetch
  const hasMoreToShow = Boolean(
    fetchNextPage && (items.length > visibleCount || hasMore),
  );

  if (isLoading) {
    return (
      <ExpandableAccordionSkeleton
        className={className}
        title={title}
        initialVisibleCount={initialVisibleCount}
      />
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-col overflow-hidden rounded-2xl border border-primary bg-primary"
      data-testid={testId}
    >
      <div className="flex items-center gap-x-2 border-b border-primary px-6 py-3">
        <Typography.LabelMd weight="semibold">{title}</Typography.LabelMd>
        {!!totalCount && totalCount > 0 && (
          <Badge
            variant={totalCountVariant}
            label={totalCount > 99 ? "99+" : totalCount.toString()}
          />
        )}
      </div>

      <ul
        className="flex list-none flex-col"
        data-testid={listTestId ?? (testId ? `${testId}-list` : undefined)}
      >
        {visibleItems.map((item, index) => (
          <div key={item?.person.public_uid}>
            <ExpandableListItem
              communityMember={item.person as CommunityMember}
              item={item}
            />
            {index < visibleItems.length - 1 && <Divider />}
          </div>
        ))}
      </ul>
      {isFetchingNextPage && <LoaderRow />}
      {hasMoreToShow && !isFetchingNextPage && (
        <div className="flex justify-center rounded-b-xl bg-secondary">
          <Button
            variant="tertiary"
            onClick={handleShowMore}
            className="w-full !rounded-none !rounded-b-xl"
          >
            {t("show_more")}
          </Button>
        </div>
      )}
    </div>
  );
};
